import axios from "axios";

export interface GeocodingResult {
  latitude: number;
  longitude: number;
  fsa: string;
  postalCode: string;
  city: string;
  province: string;
  formattedAddress: string;
}

export interface GeocodingError {
  message: string;
  code: string;
}

class GeocodingService {
  // Use a proxy endpoint to avoid CORS and referrer restrictions
  private getProxyUrl(): string {
    if (!window.config) {
      console.warn("Config not loaded yet. Please ensure the app is fully loaded before using geocoding services.");
      return "";
    }
    
    const proxyUrl = `${window.config.reco_engine_api_proxy}/api/geocode` || "";
    
    if (!proxyUrl) {
      console.warn("Google Geocoding proxy URL not found in config. Please add reco_engine_api_proxy_url to your config.json");
    }
    
    return proxyUrl;
  }

  /**
   * Geocode an address or postal code to get coordinates and FSA
   */
  async geocodeAddress(address: string): Promise<GeocodingResult> {

    try {
      // Use proxy to avoid referrer restrictions
      const response = await axios.post(this.getProxyUrl(), {
        action: "geocode",
        address: address,
      });

      if (response.data.status !== "OK") {
        throw new Error(`Geocoding failed: ${response.data.status}`);
      }

      const result = response.data.results[0];
      return this.parseGeocodingResult(result);
    } catch (error) {
      console.error("Geocoding error:", error);
      const geocodingError = new Error("Failed to geocode address", error) as any;
      geocodingError.code = "GEOCODING_ERROR";
      throw geocodingError;
    }
  }

  /**
   * Reverse geocode coordinates to get address and FSA
   */
  async reverseGeocode(latitude: number, longitude: number): Promise<GeocodingResult> {
    console.log("Attempting reverse geocoding for:", { latitude, longitude });

    // Validate coordinates
    if (!this.isValidCoordinate(latitude, longitude)) {
      throw new Error("Invalid coordinates provided");
    }

    try {
      // Use proxy to avoid referrer restrictions
      const response = await axios.post(this.getProxyUrl(), {
        action: "reverse_geocode",
        latitude: latitude,
        longitude: longitude,
      });

      console.log("Geocoding response:", response.data);

      if (response.data.status !== "OK") {
        throw new Error(`Reverse geocoding failed: ${response.data.status}`);
      }

      if (response.data.results.length === 0) {
        throw new Error("No geocoding results found");
      }

      const result = response.data.results[0];
      console.log("Selected geocoding result:", result);
      return this.parseGeocodingResult(result);
    } catch (error) {
      console.error("Reverse geocoding error:", error);
      const geocodingError = new Error("Failed to reverse geocode coordinates", error) as any;
      geocodingError.code = "REVERSE_GEOCODING_ERROR";
      throw geocodingError;
    }
  }

  /**
   * Validate if coordinates are reasonable
   */
  private isValidCoordinate(latitude: number, longitude: number): boolean {
    // Check if coordinates are within reasonable bounds for Canada
    const validLat = latitude >= 41.0 && latitude <= 84.0; // Canada latitude range
    const validLng = longitude >= -141.0 && longitude <= -52.0; // Canada longitude range
    
    if (!validLat || !validLng) {
      console.warn("Coordinates outside Canada bounds:", { latitude, longitude });
    }
    
    return validLat && validLng;
  }

  /**
   * Extract FSA from postal code (first 3 characters)
   */
  private extractFSA(postalCode: string): string {
    if (!postalCode) return "";
    
    // Remove spaces and take first 3 characters
    const cleanPostalCode = postalCode.replace(/\s/g, "");
    return cleanPostalCode.substring(0, 3).toUpperCase();
  }

  /**
   * Parse Google Geocoding API response
   */
  private parseGeocodingResult(result: any): GeocodingResult {
    const {location} = result.geometry;
    const addressComponents = result.address_components;

    let postalCode = "";
    let city = "";
    let province = "";

    // Extract address components
    for (const component of addressComponents) {
      const {types} = component;
      
      if (types.includes("postal_code")) {
        postalCode = component.long_name;
      } else if (types.includes("locality")) {
        city = component.long_name;
      } else if (types.includes("administrative_area_level_1")) {
        province = component.short_name;
      }
    }

    const fsa = this.extractFSA(postalCode);

    return {
      latitude: location.lat,
      longitude: location.lng,
      fsa,
      postalCode,
      city,
      province,
      formattedAddress: result.formatted_address,
    };
  }

  /**
   * Get FSA from existing location data or geocode if needed
   */
  async getFSAFromLocation(locationData: any): Promise<string> {
    // If location already has FSA, return it
    if (locationData?.fsa) {
      return locationData.fsa;
    }

    // If location has coordinates, reverse geocode
    if (locationData?.latitude && locationData?.longitude) {
      try {
        const result = await this.reverseGeocode(locationData.latitude, locationData.longitude);
        return result.fsa;
      } catch (error) {
        console.warn("Failed to get FSA from coordinates:", error);
        return "";
      }
    }

    // If location has address, geocode it
    if (locationData?.address?.formattedAddress) {
      try {
        const result = await this.geocodeAddress(locationData.address.formattedAddress);
        return result.fsa;
      } catch (error) {
        console.warn("Failed to get FSA from address:", error);
        return "";
      }
    }

    return "";
  }

  /**
   * Check if a string is a valid Canadian postal code
   */
  isValidCanadianPostalCode(postalCode: string): boolean {
    const regex = /^[ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTV-Z][ -]?\d[ABCEGHJ-NPRSTV-Z]\d$/i;
    return regex.test(postalCode);
  }
}

// Export singleton instance
export default new GeocodingService(); 